import React, { FC } from "react";
import { useIntl } from "react-intl";

import FormControlLabel from "../FormControlLabel";
import Typography from "../Typography";
import Checkbox, { CheckboxProps } from "../Checkbox";
import Paper from "../Paper";
import Box from "../Box";
import NumberSpinner from "../NumberSpinner";

import messages from "./messages";
import useStyles from "./useStyles";

export type CheckboxGroupOption = {
  value: string;
  label: string;
  price: number;
  currencyCode: string;
  checked?: boolean;
  allowMultiplePurchases?: boolean;
  quantity?: number;
  onQuantityChange?: (quantity: number) => void;
  disabled?: boolean;
};

interface CheckboxGroupProps extends CheckboxProps {
  title?: string;
  dense?: boolean;
  options: CheckboxGroupOption[];
}

const CheckboxGroup: FC<CheckboxGroupProps> = ({
  title,
  options,
  dense,
  ...checkboxProps
}) => {
  const intl = useIntl();
  const classes = useStyles();

  if (!options?.length) {
    return null;
  }

  return (
    <Paper classes={{ root: classes.paper }} sx={{ padding: dense ? 2 : 4 }}>
      {title && (
        <Typography variant="h3" mb={2}>
          {title}
        </Typography>
      )}

      {options.map((option) => {
        const isOptionDisabled = !!(checkboxProps.disabled || option.disabled);

        return (
          <Box
            key={option.value}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <FormControlLabel
              control={
                <Checkbox
                  {...checkboxProps}
                  checked={option.checked}
                  disabled={isOptionDisabled}
                />
              }
              classes={{ root: classes.input }}
              value={option.value}
              disabled={isOptionDisabled}
              label={
                <Typography
                  sx={{
                    wordBreak: "break-word",
                    // A Typography label is not wrapped by FormControlLabel, so it
                    // does not get the disabled color on its own
                    ...(isOptionDisabled && { color: "action.disabled" }),
                  }}
                >
                  {intl.formatMessage(messages.label, {
                    title: option.label,
                  })}
                  {intl.formatNumber(option.price, {
                    style: "currency",
                    currency: option.currencyCode,
                  })}
                </Typography>
              }
            />
            {option.allowMultiplePurchases && option.onQuantityChange && (
              <NumberSpinner
                value={option.quantity ?? 1}
                onChange={option.onQuantityChange}
                min={1}
                disabled={isOptionDisabled}
              />
            )}
          </Box>
        );
      })}
    </Paper>
  );
};

export default CheckboxGroup;
